// Ekran 14 · "Kullanıcılar & Roller" sekmesi.
//
// Referansta düzenleme yolu YOK: davet düğmesi yalnız toast atıyor, satır
// aksiyonu "Kullanıcı kartı (demo)" diyordu. İkisi de gerçek akışa bağlandı —
// "toast yan etkiyi söyler" kuralı bir yan etki olmadan tutulamaz.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { goreliZaman } from '../../lib/trFormat'
import { sifreOlc, SIFRE_ASGARI_UZUNLUK } from '../../lib/sifreGucu'
import { useOturum } from '../../lib/oturum'
import type { Rol } from '../account/api'
import {
  ayarlar as ayarApi,
  kullanicilar as kullaniciApi,
  type KullaniciSatiri,
} from './api'

const ROLLER: { anahtar: Rol; ad: string }[] = [
  { anahtar: 'tesis_sorumlusu', ad: 'Tesis Sorumlusu' },
  { anahtar: 'operator', ad: 'Vardiya Operatörü' },
  { anahtar: 'laborant', ad: 'Laborant' },
  { anahtar: 'muhasebe', ad: 'Muhasebe' },
]

const ERISIM_ETIKET: Record<string, string> = { yaz: 'RW', oku: 'RO', yok: '—' }

function hataMetni(h: unknown): string {
  if (h instanceof ApiHatasi) {
    return h.detay?.length ? `${h.message} — ${h.detay.join(' · ')}` : h.message
  }
  return 'İşlem tamamlanamadı.'
}

// ------------------------------------------------------------------ drawer

/**
 * Davet / düzenleme katmanı. Referansta yok; §8-14 "kullanıcı davet" maddesi
 * için tasarlandı. v1'de e-posta altyapısı olmadığı için davet bağlantısı
 * gönderilemiyor — başlangıç şifresini tesis sorumlusu belirliyor ve kullanıcı
 * ilk girişte değiştiriyor (profil ekranı "hiç değiştirilmedi" gösteriyor).
 */
function KullaniciFormu({
  kayit,
  tesisler,
  bitti,
}: {
  kayit: KullaniciSatiri | null
  tesisler: { id: string; ad: string }[]
  bitti: () => void
}) {
  const kuyruk = useQueryClient()
  const yeni = kayit === null

  const [eposta, setEposta] = useState(kayit?.eposta ?? '')
  const [ad, setAd] = useState(kayit?.ad ?? '')
  const [unvan, setUnvan] = useState(kayit?.unvan ?? '')
  const [tel, setTel] = useState(kayit?.tel ?? '')
  const [rol, setRol] = useState<Rol>(kayit?.rol ?? 'operator')
  const [durum, setDurum] = useState(kayit?.durum ?? 'aktif')
  const [onay, setOnay] = useState(kayit?.yetkiOnay ?? false)
  const [storno, setStorno] = useState(kayit?.yetkiStorno ?? false)
  const [sifre, setSifre] = useState('')
  const [secili, setSecili] = useState<string[]>(
    yeni ? tesisler.map((t) => t.id) : tesisler.filter((t) => kayit!.tesisler.includes(t.ad)).map((t) => t.id),
  )

  const olcum = sifreOlc(sifre)

  const kaydet = useMutation({
    mutationFn: async () => {
      if (yeni) {
        return kullaniciApi.davet({
          eposta,
          ad,
          unvan: unvan || null,
          tel: tel || null,
          rol,
          yetkiOnay: onay,
          yetkiStorno: storno,
          tesisIdler: secili,
          baslangicSifresi: sifre,
        })
      }
      return kullaniciApi.guncelle(kayit!.id, {
        ad,
        unvan: unvan || null,
        tel: tel || null,
        rol,
        durum,
        yetkiOnay: onay,
        yetkiStorno: storno,
        tesisIdler: secili,
      })
    },
    onSuccess: async () => {
      await kuyruk.invalidateQueries({ queryKey: ['kullanicilar'] })
      await kuyruk.invalidateQueries({ queryKey: ['rol-matrisi'] })
      toast(
        yeni
          ? `${eposta} oluşturuldu — kullanıcı ilk girişte şifresini değiştirmeli`
          : `${ad} güncellendi — denetim izine işlendi`,
      )
      bitti()
    },
    onError: (h) => toast(hataMetni(h)),
  })

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label htmlFor="ku-ad">Ad Soyad</label>
            <input id="ku-ad" className="in" value={ad} onChange={(e) => setAd(e.target.value)} />
          </div>
          <div className="fld">
            <label htmlFor="ku-eposta">E-posta</label>
            <input
              id="ku-eposta"
              className="in"
              value={eposta}
              readOnly={!yeni}
              onChange={(e) => setEposta(e.target.value)}
            />
            {!yeni && <div className="fld-hint">E-posta oturum kimliğidir; değiştirilemez.</div>}
          </div>
          <div className="fld">
            <label htmlFor="ku-unvan">Ünvan</label>
            <input
              id="ku-unvan"
              className="in"
              value={unvan}
              onChange={(e) => setUnvan(e.target.value)}
            />
          </div>
          <div className="fld">
            <label htmlFor="ku-tel">Telefon</label>
            <input
              id="ku-tel"
              className="in mono"
              value={tel}
              onChange={(e) => setTel(e.target.value)}
            />
          </div>
          <div className="fld">
            <label htmlFor="ku-rol">Rol</label>
            <select
              id="ku-rol"
              className="in"
              value={rol}
              onChange={(e) => setRol(e.target.value as Rol)}
            >
              {ROLLER.map((r) => (
                <option key={r.anahtar} value={r.anahtar}>
                  {r.ad}
                </option>
              ))}
            </select>
          </div>
          {!yeni && (
            <div className="fld">
              <label htmlFor="ku-durum">Durum</label>
              <select
                id="ku-durum"
                className="in"
                value={durum}
                onChange={(e) => setDurum(e.target.value as 'aktif' | 'pasif')}
              >
                <option value="aktif">Aktif</option>
                <option value="pasif">Pasif</option>
              </select>
              <div className="fld-hint">Pasife alınan kullanıcının açık oturumları kapanır.</div>
            </div>
          )}
        </div>

        {yeni && (
          <div className="fld" style={{ marginTop: 10 }}>
            <label htmlFor="ku-sifre">Başlangıç şifresi</label>
            <input
              id="ku-sifre"
              className="in"
              type="password"
              placeholder={`En az ${SIFRE_ASGARI_UZUNLUK} karakter`}
              value={sifre}
              onChange={(e) => setSifre(e.target.value)}
            />
            <div className="pwbar">
              <i style={{ width: olcum.genislik, background: olcum.renk }} />
            </div>
            <div className="fld-hint">
              Güç: {olcum.etiket} — v1'de davet e-postası gönderilmiyor; şifreyi kullanıcıya siz
              iletirsiniz, ilk girişte değiştirmesi gerekir.
            </div>
          </div>
        )}

        <div className="insp-sec-t" style={{ marginTop: 12 }}>
          Tesis erişimi (K1)
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          {tesisler.map((t) => {
            const acik = secili.includes(t.id)
            return (
              <label key={t.id} className={acik ? 'chip on' : 'chip'} style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={acik}
                  style={{ marginRight: 4 }}
                  onChange={() =>
                    setSecili((s) => (acik ? s.filter((x) => x !== t.id) : [...s, t.id]))
                  }
                />
                {t.ad}
              </label>
            )
          })}
        </div>

        <div className="insp-sec-t" style={{ marginTop: 12 }}>
          Ek yetkiler
        </div>
        <div className="switch">
          <span
            className={onay ? 'sw on' : 'sw'}
            role="switch"
            aria-checked={onay}
            aria-label="Beyan onayı"
            tabIndex={0}
            onClick={() => setOnay((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOnay((v) => !v) }
            }}
          />
          Beyan onayı (K16)
        </div>
        <div className="switch">
          <span
            className={storno ? 'sw on' : 'sw'}
            role="switch"
            aria-checked={storno}
            aria-label="Storno"
            tabIndex={0}
            onClick={() => setStorno((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStorno((v) => !v) }
            }}
          />
          Storno (K15 · K39)
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={bitti}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={kaydet.isPending}
          onClick={() => kaydet.mutate()}
        >
          {yeni ? 'Kullanıcıyı oluştur' : 'Kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}

// ------------------------------------------------------------------- sekme

export default function KullanicilarSekmesi() {
  const { ac, kapat } = useDrawer()
  const { durum } = useOturum()

  const liste = useQuery({ queryKey: ['kullanicilar'], queryFn: kullaniciApi.liste })
  const matris = useQuery({ queryKey: ['rol-matrisi'], queryFn: ayarApi.rolMatrisi })

  // Tesis listesi oturumdan: kullanıcının erişebildiği tesisler zaten girişte
  // geldi, ayrı bir uç açmaya gerek yok.
  const tesisler = durum?.tesis ? [{ id: durum.tesis.id, ad: durum.tesis.ad }] : []

  function formuAc(kayit: KullaniciSatiri | null) {
    ac({
      baslik: kayit ? kayit.ad : 'Kullanıcı davet et',
      altBaslik: kayit ? kayit.eposta : 'Rol, tesis erişimi ve ek yetkiler',
      boyut: 'md',
      icerik: <KullaniciFormu kayit={kayit} tesisler={tesisler} bitti={kapat} />,
    })
  }

  return (
    <>
      <div className="card">
        <div className="toolbar">
          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy-800)' }}>
            Kullanıcılar
          </span>
          <div className="toolbar-right">
            <button className="btn btn-sec btn-sm" type="button" onClick={() => formuAc(null)}>
              + Kullanıcı davet et
            </button>
          </div>
        </div>

        <table className="grid">
          <thead>
            <tr>
              <th>Kullanıcı</th>
              <th style={{ width: 150 }}>Rol</th>
              <th style={{ width: 110 }}>Durum</th>
              <th style={{ width: 150 }}>Son giriş</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {(liste.data ?? []).map((k) => (
              <tr
                key={k.id}
                className={k.durum === 'pasif' ? 'storno-r' : undefined}
                onClick={() => formuAc(k)}
              >
                <td>
                  <div className="cell-name">
                    {k.ad}
                    {k.kendisi && (
                      <span className="badge b-navy" style={{ marginLeft: 4 }}>
                        siz
                      </span>
                    )}
                  </div>
                  <div className="cell-sub">{k.eposta}</div>
                </td>
                <td>{k.rolAdi}</td>
                <td>
                  {k.durum === 'aktif' ? (
                    <span className="badge b-navy">
                      <span className="dot" />
                      Aktif
                    </span>
                  ) : (
                    <span className="badge b-amber">Pasif</span>
                  )}
                </td>
                <td style={{ fontSize: 11 }}>{goreliZaman(k.sonGiris)}</td>
                <td>
                  <div className="row-acts">
                    <button
                      className="mini-btn"
                      type="button"
                      title="Kullanıcı kartını aç"
                      onClick={(e) => {
                        e.stopPropagation()
                        formuAc(k)
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: 8 }}>
        <div className="sect-t">
          Rol İzin Matrisi
          <span className="cell-sub" style={{ marginLeft: 6 }}>
            RW yazma · RO okuma · — erişim yok
          </span>
        </div>
        <table className="ptab">
          <thead>
            <tr>
              <th>Modül</th>
              {(matris.data?.roller ?? []).map((r) => (
                <th key={r.anahtar}>{r.kisaAd}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(matris.data?.moduller ?? []).map((m) => (
              <tr key={m.anahtar}>
                <td>{m.ad}</td>
                {(matris.data?.roller ?? []).map((r) => {
                  const e = matris.data!.matris[m.anahtar]?.[r.anahtar] ?? 'yok'
                  // RW/RO/— bir sayı değil; `num` mono+11,5px veriyordu, matris
                  // referanstaki gövde tipografisiyle okunmalı (referans 582).
                  return <td key={r.anahtar}>{ERISIM_ETIKET[e] ?? e}</td>
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pnote" style={{ margin: '8px 12px' }}>
          {matris.data?.dipnot ??
            'Onay ve storno yetkileri rolden ayrıca işaretlenir (K15 · K16).'}{' '}
          Her kullanım denetim izinde <b>kim · ne zaman · gerekçe</b> ile saklanır.
        </div>
      </div>
    </>
  )
}
